/**
 * @file fetchMetricFile.ts
 * @description Fetches a public metric JSON file and adapts it into a
 * `DistrictDict` keyed by district identifiers.
 *
 * Expected public file path:
 *   /public/data/congress{congressId}/metrics/{metricKey}.json
 * Example:
 *   /public/data/congress118/metrics/veteran_count.json
 */

import { asset } from '@/utils/asset';
import { parseMetricToDistrictDict } from './parseMetric';
import type { DistrictDict, StatsBundle } from '@/types';

type MetricJson = {
  metadata?: {
    title?: string;
    recommended?: string;
  };
  data?: Record<string, number>;
  statistics?: Record<string, StatsBundle>;
};

function adaptMetricFile(
  metricJson: unknown,
  ctx: { congressId: number; metricKey: string },
): {
  metricKey: string;
  congressId: number;
  statistics: Record<string, StatsBundle>;
  metadata: { title?: string; recommended?: string } | undefined;
  data: Record<string, number>;
} {
  const json = metricJson as MetricJson;
  const rawStats = json?.statistics ?? {};

  // Sanitize and normalize statistics entries. Handle common misspellings
  // and coerce numeric-like values to numbers so UI can rely on consistent types.
  const statistics: Record<string, StatsBundle> = Object.fromEntries(
    Object.entries(rawStats).map(([k, v]) => {
      const tstatRaw = (v as any)?.tstat ?? [];
      const pRaw = (v as any)?.p ?? [];
      const dfRaw = (v as any)?.df ?? [];
      // Some files mistakenly use the key 'significanct' — accept either.
      const significantRaw = (v as any)?.significant ?? (v as any)?.significanct ?? [];

      const tstat = Array.isArray(tstatRaw) ? tstatRaw.map((x) => Number(x)) : [];
      const p = Array.isArray(pRaw) ? pRaw.map((x) => Number(x)) : [];
      const df = Array.isArray(dfRaw) ? dfRaw.map((x) => Number(x)) : [];
      const significant = Array.isArray(significantRaw) ? significantRaw.map(String) : [];

      return [k, { tstat, p, df, significant }];
    }),
  );

  return {
    metricKey: ctx.metricKey,
    congressId: ctx.congressId,
    statistics,
    metadata: json?.metadata,
    data: json?.data ?? {},
  };
}

export async function fetchMetricFile(
  congressId: number,
  metricKey: string,
): Promise<{
  districts: DistrictDict;
  statistics: Record<string, StatsBundle>;
  recommendedBillId?: string;
}> {
  const url = asset(`data/congress${congressId}/metrics/${metricKey}.json`);
  const res = await fetch(url);
  if (!res.ok) throw new Error(`[fetchMetricFile] HTTP ${res.status} for ${url}`);

  // I don't know why I need this but apparently it solved my parsing issues
  const rawText = await res.text();
  const safeText = rawText
    .replace(/\bNaN\b/g, 'null')
    .replace(/\bInfinity\b/g, 'null')
    .replace(/\b-Infinity\b/g, 'null');

  let json: unknown;
  try {
    json = JSON.parse(safeText);
  } catch (e) {
    throw new Error(`[fetchMetricFile] invalid JSON for ${url}: ${String(e)}`);
  }

  const metric = adaptMetricFile(json, { congressId, metricKey });

  // Extract metric label from metadata, fallback to metricKey
  const metricLabel = metric.metadata?.title ?? metricKey;

  const districts = parseMetricToDistrictDict(metric.data, metricLabel);

  return {
    districts,
    statistics: metric.statistics,
    recommendedBillId: metric.metadata?.recommended,
  };
}
